import { useCallback, useEffect, useRef } from 'react';
import {
  orchestrateInventoryCalculation,
  applyInventoryDataPatch
} from '../../../domain/inventory/inventoryCalculationOrchestrator';
import { STOCK_RECALCULATED_EVENT } from '../../../domain/inventory/recalculation';

export function useInventoryCalculationLifecycle({
  dataLoaded,
  products,
  lots,
  purchases,
  sales,
  inventoryData,
  setInventoryData
}) {
  const inventoryMetaRef = useRef({
    lastDataHash: inventoryData.lastDataHash,
    lastCalculated: inventoryData.lastCalculated
  });
  const stockCalcUiDepthRef = useRef(0);

  useEffect(() => {
    inventoryMetaRef.current = {
      lastDataHash: inventoryData.lastDataHash,
      lastCalculated: inventoryData.lastCalculated
    };
  }, [inventoryData.lastDataHash, inventoryData.lastCalculated]);

  const calculateInventoryData = useCallback(async ({ showCalculating = false } = {}) => {
    if (!dataLoaded || products.length === 0) {
      if (showCalculating && stockCalcUiDepthRef.current > 0) {
        stockCalcUiDepthRef.current = 0;
        setInventoryData((prev) =>
          prev.isCalculating ? { ...prev, isCalculating: false } : prev
        );
      }
      return;
    }

    // Inventaire: DataContext + local idle valuation — skip silent InstantCache full recalc.
    if (!showCalculating) {
      return;
    }

    stockCalcUiDepthRef.current += 1;
    setInventoryData((prev) => ({ ...prev, isCalculating: true }));

    const safetyTimer = setTimeout(() => {
      stockCalcUiDepthRef.current = 0;
      setInventoryData((prev) =>
        prev.isCalculating ? { ...prev, isCalculating: false } : prev
      );
    }, 12000);

    try {
      const orchestration = await orchestrateInventoryCalculation({
        products,
        lots,
        purchases,
        sales,
        lastDataHash: inventoryMetaRef.current.lastDataHash,
        lastCalculated: inventoryMetaRef.current.lastCalculated
      });

      if (orchestration.skipped) {
        return;
      }

      applyInventoryDataPatch(setInventoryData, orchestration.patch);
    } finally {
      clearTimeout(safetyTimer);
      stockCalcUiDepthRef.current = Math.max(0, stockCalcUiDepthRef.current - 1);
      if (stockCalcUiDepthRef.current === 0) {
        setInventoryData((prev) =>
          prev.isCalculating ? { ...prev, isCalculating: false } : prev
        );
      }
    }
  }, [dataLoaded, products, lots, purchases, sales, setInventoryData]);

  // Lots/Achats → stockRecalculated (Cache wurde schon im Orchestrator aktualisiert)
  useEffect(() => {
    const onStockRecalculated = (event) => {
      const { showInventoryOverlay = false } = event.detail || {};
      if (!showInventoryOverlay) return;
      void calculateInventoryData({ showCalculating: true });
    };

    window.addEventListener(STOCK_RECALCULATED_EVENT, onStockRecalculated);
    return () => window.removeEventListener(STOCK_RECALCULATED_EVENT, onStockRecalculated);
  }, [calculateInventoryData]);

  // Stale Overlay beim Mount/Unmount nie hängen lassen
  useEffect(() => {
    return () => {
      stockCalcUiDepthRef.current = 0;
      setInventoryData((prev) =>
        prev.isCalculating ? { ...prev, isCalculating: false } : prev
      );
    };
  }, [setInventoryData]);

  return { calculateInventoryData };
}
